import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { headerSty, contentSty } from "../table-styles";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { Dot } from "../UIElements/svgIcons";
import { useNavigate } from "react-router-dom";
import { ScheduleContentsTablePropTypes } from "../../interface/schedule";
import { formatDateTime } from "../../utils/url-id";

export default function ScheduleTable({
  scheduleList,
  loading,
}: ScheduleContentsTablePropTypes) {
  const theme = useTheme();
  const navigate = useNavigate();
  const headStyles = {
    ...headerSty,
    color: theme.palette.neutral.main,
    textAlign: "center",
  };
  const contentStyles = {
    ...contentSty,
    textAlign: "center",
    padding: "8px 12px",
  };

  const [openRow, setOpenRow] = React.useState<number | null>(null); // Track which row is open

  const handleToggleOpen = (rowId: number) => {
    setOpenRow((prevOpenRow) => (prevOpenRow === rowId ? null : rowId));
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        my: 1,
        boxShadow: "none",
        width: "96%",
        mx: "auto",
        minHeight: 400,
      }}
    >
      <Table
        sx={{
          minWidth: 650,
          boxShadow: "none",
          my: 1,
          paddingRight: 2,
        }}
        aria-label="simple table"
      >
        <TableHead sx={{ paddingX: 2 }}>
          <TableRow>
            <TableCell sx={{ ...headStyles, width: "15%" }}>
              Schedule ID
            </TableCell>
            <TableCell sx={{ ...headStyles, width: "15%" }}>
              Schedule Name
            </TableCell>
            <TableCell sx={{ ...headStyles }}>Shifts</TableCell>
            <TableCell sx={{ ...headStyles }}>Date/Time</TableCell>
            {/* <TableCell sx={{ ...headStyles }}>Status</TableCell> */}
          </TableRow>
        </TableHead>
        <TableBody>
          {scheduleList?.map((row: any, index: number) => (
            <TableRow
              key={row?._id}
              onClick={() => navigate(`/schedule/${row?._id}`)}
            >
              <TableCell sx={contentStyles}>{row._id}</TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {row?.schedule_name}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {row?.shifts?.length ?? 0}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatDateTime(row.created_at)}
              </TableCell>
              {/* <TableCell sx={{ ...contentStyles }}>
               <TimesheetEmpStatus status={row?.status} />
              </TableCell> */}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {scheduleList?.length === 0 && !loading && (
        <Box
          sx={{
            height: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
          >
            No Schedule Available.
          </Typography>
        </Box>
      )}
      {loading && (
        <Box sx={{ width: "96%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
        </Box>
      )}
    </TableContainer>
  );
}

export const TimesheetEmpStatus: React.FC<{
  status: "pending" | "draft" | "active" | string;
}> = ({ status }) => {
  const theme = useTheme();

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "pending":
        return {
          color: theme.palette.warning.main,
          backgroundColor: "#ffac336b",
        };
      case "draft":
        return {
          color: theme.palette.error.main,
          backgroundColor: "#f6685e6b",
        };
      case "active":
        return {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.lightBlue.main,
        };
      default:
        return {
          color: theme.palette.success.main,
          backgroundColor: theme.palette.success.light,
        };
    }
  };

  const statusStyles = getStatusStyles(status);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      sx={{ width: 100, mx: "auto" }}
    >
      <Box
        sx={{
          marginX: 1,
          paddingY: 1,
          paddingX: 3,
          borderRadius: "3px",
          fontSize: 12,
          fontFamily: "Lexend",
          textTransform: "uppercase",
          ...statusStyles,
        }}
      >
        {status}
      </Box>
    </Stack>
  );
};
